import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import type { FieldPolicy } from '@apollo/client';

function resolveApiUrl(): string {
  const raw = process.env.NEXT_PUBLIC_GRAPHQL_API_URL ?? '/graphql';
  // Browser: relative URL goes through the Next.js rewrite (see next.config.ts).
  if (typeof window !== 'undefined') return raw;
  // Server-side (Server Components, Route Handlers): Node fetch needs absolute.
  if (raw.startsWith('/')) {
    return `http://localhost:4001${raw}`;
  }
  return raw;
}
const API_URL = resolveApiUrl();

// ============================================================================
// Relay-style pagination merge helper
// ============================================================================

/**
 * Generic merge function for Relay connection types.
 * Appends new edges on fetchMore, replaces on fresh query.
 */
function relayConnectionMerge(): FieldPolicy {
  return {
    keyArgs: false,
    merge(existing, incoming, { args }) {
      // Fresh query (no cursor) — replace entirely
      if (!args?.after) {
        return incoming;
      }
      // Paginated fetchMore — append edges
      return {
        ...incoming,
        edges: [...(existing?.edges ?? []), ...incoming.edges],
      };
    },
  };
}

// ============================================================================
// InMemoryCache with TypePolicies
// ============================================================================

function createCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // Admin params included in all keyArgs so the cache separates
          // "my data" from "all customers" responses correctly.

          // Paginated queries — merge on fetchMore
          documents: {
            ...relayConnectionMerge(),
            keyArgs: ['filter', 'sortBy', 'sortDirection', 'allCustomers', 'customerNumbers'],
          },
          articleHistory: {
            ...relayConnectionMerge(),
            keyArgs: ['input', 'allCustomers', 'customerNumbers'],
          },
          customerArticles: {
            ...relayConnectionMerge(),
            keyArgs: [
              'search',
              'documentTypes',
              'dateFrom',
              'dateTo',
              'onlyFavorites',
              'sortBy',
              'sortDirection',
              'first', // trennt Discovery (first:5) von List (first:20) sauber im Cache
              'allCustomers',
              'customerNumbers',
            ],
          },
          materialRequests: {
            ...relayConnectionMerge(),
            keyArgs: ['status', 'allCustomers', 'customerNumbers'],
          },

          // Single lookups — keyed by their argument
          document: { keyArgs: ['id', 'allCustomers', 'customerNumbers'] },
          documentChain: { keyArgs: ['documentId', 'allCustomers', 'customerNumbers'] },
          materialTemplate: { keyArgs: ['id', 'allCustomers', 'customerNumbers'] },
          materialRequest: { keyArgs: ['id', 'allCustomers', 'customerNumbers'] },
          article: { keyArgs: ['articleNumber'] },
          articleByBarcode: { keyArgs: ['barcode'] },

          // Queries without explicit keyArgs but that need admin separation
          dashboardStats: { keyArgs: ['allCustomers', 'customerNumbers'] },
          documentStats: { keyArgs: ['allCustomers', 'customerNumbers'] },
          recentDocuments: { keyArgs: ['limit', 'allCustomers', 'customerNumbers'] },
          commissions: { keyArgs: ['allCustomers', 'customerNumbers'] },
          locationCodes: { keyArgs: ['allCustomers', 'customerNumbers'] },
          salesRepresentative: { keyArgs: ['documentNumber', 'allCustomers', 'customerNumbers'] },
          favorites: { keyArgs: ['allCustomers', 'customerNumbers'] },
          favoriteLabels: { keyArgs: ['allCustomers', 'customerNumbers'] },
          pinnedFavorites: { keyArgs: ['limit', 'allCustomers', 'customerNumbers'] },
          materialTemplates: { keyArgs: ['allCustomers', 'customerNumbers'] },
        },
      },

      // Normalize by unique ID fields
      Document: { keyFields: ['id'] },
      FavoriteItem: { keyFields: ['articleNumber'] },
      FavoriteLabel: { keyFields: ['name'] },
      MaterialTemplate: { keyFields: ['id'] },
      MaterialRequest: { keyFields: ['id'] },
      MaterialRequestItem: { keyFields: ['id'] },
      Article: { keyFields: ['articleNumber'] },
      CustomerArticle: { keyFields: ['articleNumber'] },
      RelatedDocument: { keyFields: ['id'] },
    },
  });
}

// ============================================================================
// Apollo Client Factory
// ============================================================================

/**
 * Creates an Apollo Client with dynamic auth token injection.
 *
 * @param getToken — callback that returns the current access token.
 *                   Called on every request so token refresh is automatic.
 * @param onGraphQLErrors — optional; erhält die Fehlermeldungen jeder
 *                   GraphQL-Antwort samt der Information, ob überhaupt ein
 *                   Token mitgeschickt wurde. Bewusst ohne eigene Deutung:
 *                   die Auswertung liegt in feature-auth
 *                   (`classifyAuthRejection`), damit diese Datei frei von
 *                   Auth-Fachlogik bleibt und weiter serverseitig
 *                   verwendbar ist (siehe server-client.ts, das den Callback
 *                   nicht setzt — dort gibt es keine UI, die reagieren
 *                   könnte).
 */
export function createApolloClient(
  getToken: () => string | undefined,
  onGraphQLErrors?: (messages: string[], hadToken: boolean) => void
): ApolloClient {
  const authLink = new ApolloLink((operation, forward) => {
    const token = getToken();
    if (token) {
      operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        },
      }));
    }
    return forward(operation);
  });

  // Liegt NACH authLink, damit beim Fehler bereits feststeht, ob die Anfrage
  // ein Token trug.
  const errorLink = new ErrorLink(({ error }) => {
    if (!onGraphQLErrors) return;
    if (!CombinedGraphQLErrors.is(error)) return;
    onGraphQLErrors(
      error.errors.map((e) => e.message),
      !!getToken()
    );
  });

  const httpLink = new HttpLink({ uri: API_URL });

  return new ApolloClient({
    link: from(
      onGraphQLErrors
        ? [authLink, errorLink, httpLink]
        : [authLink, httpLink]
    ),
    cache: createCache(),
    // Default fetchPolicy is 'cache-first' — we don't override it.
    // Queries that need fresh data use 'cache-and-network' individually.
    devtools: {
      enabled: process.env.NODE_ENV === 'development',
    },
  });
}
